import { type FC } from 'react'
import { type AllWorkflowStatusesAndNullish } from '@opallabs/concepts'
import { type GemIconName } from '@opallabs/constellation'
import { gemVar, Spacing, ThemedColors } from '@opallabs/gem'
import { Icon2, type Icon2Props } from '../Icon2'

export const WorkflowIconLabel = {
  'approval-approved': 'Approved',
  'approval-denied': 'Approval Denied',
  'approval-not-sent': 'Approval Not Sent',
  'approval-pending': 'Approval Pending',
  'delivery-not-sent': 'Delivery Not Sent',
  'delivery-at-risk': 'Delivery Risk',
  'delivery-error': 'Delivery Error',
  'delivery-complete': 'Delivery Complete',
  published: 'Published',
  'not-created': 'No Workflow',
  'not-started': 'Not Started',
  'tasks-pending': 'Tasks Pending',
  'tasks-complete': 'Tasks Complete',
  paused: 'Paused',
} as const satisfies Record<AllWorkflowStatusesAndNullish, string>

export type WorkflowIconProps = Omit<Icon2Props, 'glyph'> &
  Readonly<{
    /**
     * Whether to automatically switch to the `-filled` icon variant when `size` is below an undocumented threshold
     *
     * @default true
     */
    automaticallyFillAtSmallSize?: boolean
    color?: string
    status: AllWorkflowStatusesAndNullish
  }>

const icons = {
  'approval-not-sent': {
    color: gemVar(ThemedColors.IconSecondary),
    glyph: 'gem-dash-circle',
  },
  'approval-pending': {
    color: gemVar(ThemedColors.IconWarning),
    glyph: 'gem-thumbs-up-dashed-circle',
  },
  'approval-approved': {
    color: gemVar(ThemedColors.IconSuccess),
    glyph: 'gem-thumbs-up-circle',
  },
  'approval-denied': {
    color: gemVar(ThemedColors.IconError),
    glyph: 'gem-thumbs-down-circle',
  },
  'delivery-not-sent': {
    color: gemVar(ThemedColors.IconSecondary),
    glyph: 'gem-dash-delivery',
  },
  'delivery-at-risk': {
    color: gemVar(ThemedColors.IconWarning),
    glyph: 'gem-warning-delivery',
  },
  'delivery-error': {
    color: gemVar(ThemedColors.IconError),
    glyph: 'gem-close-delivery',
  },
  'delivery-complete': {
    color: gemVar(ThemedColors.IconSuccess),
    glyph: 'gem-circle-delivery',
  },
  published: {
    color: gemVar(ThemedColors.IconPublished),
    glyph: 'gem-check-delivery',
  },
  'not-created': {
    color: gemVar(ThemedColors.IconSecondary),
    glyph: 'gem-circle',
  },
  'not-started': {
    color: gemVar(ThemedColors.IconSecondary),
    glyph: 'gem-circle',
  },
  'tasks-pending': {
    color: gemVar(ThemedColors.IconWarning),
    glyph: 'gem-check-dashed-circle',
  },
  'tasks-complete': {
    color: gemVar(ThemedColors.IconSuccess),
    glyph: 'gem-check-circle',
  },
  paused: {
    color: gemVar(ThemedColors.IconSecondary),
    glyph: 'gem-pause-circle',
  },
} as const satisfies Record<
  AllWorkflowStatusesAndNullish,
  Readonly<{ color: string; glyph: GemIconName }>
>

export const WorkflowIcon: FC<WorkflowIconProps> = ({
  automaticallyFillAtSmallSize = true,
  color: customColor,
  label: customLabel,
  size = Spacing.M,
  status,
  ...otherProps
}) => {
  const { color, glyph } = icons[status]
  const actualGlyph =
    !automaticallyFillAtSmallSize ||
    // There's no `gem-circle-filled`
    glyph === 'gem-circle' ||
    size > Spacing.M
      ? glyph
      : (`${glyph}-filled` as GemIconName)

  return (
    <Icon2
      css={{ color: customColor || color }}
      glyph={actualGlyph}
      label={customLabel ?? WorkflowIconLabel[status]}
      size={size}
      {...otherProps}
    />
  )
}
WorkflowIcon.displayName = 'Lapidary: WorkflowIcon'
